import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import type { AuthUser } from '@core/core.public-api';
import {
  AuthStore,
  IdleTimeoutService,
  RealtimeConnectionService,
  SwUpdateService,
  TenantContextService,
} from '@core/core.public-api';
// Import profundo deliberado: el barrel completo de shared arrastra map/charts al bundle inicial.
import { IconSpriteComponent } from '@shared/ui/atoms/icon/icon-sprite.component';
import { ButtonComponent } from '@shared/ui/atoms/button/button.component';
import { AlertBannerComponent } from '@shared/ui/molecules/alert-banner/alert-banner.component';
import { ModalComponent } from '@shared/ui/organisms/modal/modal.component';

import { TopbarComponent } from './topbar/topbar.component';
import { SidenavComponent } from './sidenav/sidenav.component';

/**
 * Nombres legibles de tenant por UUID. El claim del JWT solo trae el UUID y no queremos
 * mostrar el UUID crudo en el chrome.
 * TODO: resolver el nombre por API (catalogo de tenants) en lugar de este mapa provisorio.
 */
const TENANT_LABELS: Readonly<Record<string, string>> = {
  '018f0000-0000-7000-8000-000000000001': 'Pampa Energía',
};

/**
 * Etiqueta neutra cuando `/auth/me` falló: la sesion es valida pero el perfil no
 * llego. No se inventa una identidad (ni rol ni UUID) — solo se deja de mentir.
 */
const UNKNOWN_USER_LABEL = 'Usuario';

/** Breakpoint desktop (Tailwind `lg`). Debajo, el sidenav es drawer off-canvas. */
const DESKTOP_MQ = '(min-width: 1024px)';

/** Persistencia del colapso del sidenav (solo desktop) entre recargas/full reloads. */
const SIDENAV_COLLAPSED_KEY = 'pvp.layout.sidenav-collapsed';

function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDENAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStoredCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDENAV_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Storage bloqueado (modo privado/embebido): el colapso simplemente no persiste.
  }
}

/**
 * Nombre visible del usuario. El nombre real lo trae `GET /auth/me` (el JWT solo
 * lleva el `sub`, un UUID opaco): mientras el perfil vuela devuelve vacio — la
 * topbar rinde un skeleton — y si el perfil fallo, la etiqueta neutra. El UUID
 * nunca llega al chrome visible (sigue disponible en el tooltip via userDetail).
 */
export function resolveUserDisplayName(user: AuthUser | null, profileLoading: boolean): string {
  if (!user) {
    return '';
  }
  const name = user.name.trim();
  if (name) {
    return name;
  }
  return profileLoading ? '' : UNKNOWN_USER_LABEL;
}

/**
 * Shell del area autenticada. Container "smart": orquesta los signals de sesion/tenant/conexion
 * de core y los baja como inputs a los componentes dumb del chrome (topbar/sidenav).
 * Montado como componente padre de las rutas protegidas (ver app.routes.ts).
 * Los breadcrumbs viven integrados a la topbar (una sola barra).
 *
 * Renderiza UNA vez el sprite de iconos del design system (pvp-icon-sprite): sin el,
 * los `<use href="#icon-...">` de pvp-icon no resuelven.
 *
 * Ademas abre la conexion realtime al entrar al area autenticada (core la deja "bajo demanda").
 */
@Component({
    selector: 'app-main-layout',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterOutlet,
        TopbarComponent,
        SidenavComponent,
        IconSpriteComponent,
        AlertBannerComponent,
        ButtonComponent,
        ModalComponent,
    ],
    template: `
    <pvp-icon-sprite />

    <div
      class="layout"
      [class.layout--collapsed]="collapsed()"
      [class.layout--mobile-open]="mobileOpen()"
    >
      <app-sidenav class="layout__sidenav" [collapsed]="collapsed() && isDesktop()" />

      @if (mobileOpen()) {
        <div class="layout__backdrop" (click)="closeMobileSidenav()" aria-hidden="true"></div>
      }

      <div class="layout__main">
        <app-topbar
          [userName]="userDisplayName()"
          [userNameLoading]="auth.isProfileLoading()"
          [userDetail]="userDetail()"
          [tenantLabel]="tenantLabel()"
          [tenantId]="tenant.tenantId()"
          [isImpersonating]="tenant.isImpersonating()"
          [connectionHealth]="realtime.health()"
          [showChangePassword]="canChangePassword()"
          (toggleSidenav)="toggleSidenav()"
          (resetTenant)="tenant.resetTenant()"
          (logout)="auth.logout()"
        />

        <main class="layout__content" role="main">
          <router-outlet />
        </main>
      </div>

      @if (auth.isDevSession()) {
        <span class="layout__dev-badge" role="status">DEV SESSION</span>
      }

      <!-- Aviso de versión nueva de la PWA (solo llega en prod: en dev el SW está deshabilitado). -->
      @if (swUpdate.updateReady()) {
        <div class="layout__update-banner">
          <pvp-alert-banner
            type="info"
            title="Hay una versión nueva"
            message="Actualizá para cargar los últimos cambios."
            [dismissible]="true"
            (dismissed)="swUpdate.dismiss()"
          >
            <div class="mt-2">
              <pvp-button size="sm" [loading]="swUpdate.activating()" (clicked)="applyUpdate()">
                Actualizar
              </pvp-button>
            </div>
          </pvp-alert-banner>
        </div>
      }

      <!-- Aviso previo al auto-logout por inactividad (LION-794). Reusa el pvp-modal
           del DS (focus-trap, Escape, restauración de foco). Cerrar el modal
           (Escape/backdrop/✕) equivale a "seguir conectado": es señal de presencia.
           Eager a propósito: es un aviso de seguridad y debe renderizar SIEMPRE al
           dispararse (un chunk lazy podría fallar justo en el primer aviso, offline,
           y el logout ocurriría sin haberlo mostrado). -->
      @if (idle.warningActive()) {
        <pvp-modal
          title="Tu sesión está por cerrarse"
          size="md"
          (closed)="idle.continueSession()"
        >
          <p class="text-sm text-text-muted">
            Por inactividad, tu sesión se cerrará en
            <strong class="text-text">{{ idle.remainingSeconds() }}</strong>
            {{ idle.remainingSeconds() === 1 ? 'segundo' : 'segundos' }}.
          </p>
          <ng-container ngProjectAs="[slot=footer]">
            <pvp-button variant="ghost" (clicked)="idle.logoutNow()">
              Cerrar sesión ahora
            </pvp-button>
            <pvp-button (clicked)="idle.continueSession()">Seguir conectado</pvp-button>
          </ng-container>
        </pvp-modal>
      }
    </div>
  `,
    styles: [
        `
      /* Mobile-first (<lg): sidenav off-canvas como drawer fixed sobre backdrop. */
      .layout {
        min-height: 100vh;
        background: var(--pvp-color-bg);
      }
      .layout__sidenav {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        display: block;
        width: 260px;
        z-index: 50;
        transform: translateX(-100%);
        transition: transform 200ms ease;
      }
      .layout--mobile-open .layout__sidenav {
        transform: translateX(0);
        box-shadow: var(--pvp-shadow-card);
      }
      .layout__backdrop {
        position: fixed;
        inset: 0;
        z-index: 40;
        background: rgba(0, 0, 0, 0.6);
      }
      .layout__main {
        display: flex;
        flex-direction: column;
        min-width: 0;
        min-height: 100vh;
      }
      .layout__content {
        flex: 1;
        padding: var(--pvp-space-md);
        overflow: auto;
      }
      @media (prefers-reduced-motion: reduce) {
        .layout__sidenav {
          transition: none;
        }
      }

      /* Desktop (>=lg): sidenav estatico en grid, colapsable a rail (comportamiento previo). */
      @media (min-width: 1024px) {
        .layout {
          display: grid;
          grid-template-columns: 260px 1fr;
        }
        .layout--collapsed {
          grid-template-columns: 68px 1fr;
        }
        .layout__sidenav {
          position: static;
          width: auto;
          z-index: auto;
          transform: none;
          transition: none;
          box-shadow: none;
        }
        .layout__main {
          min-height: 0;
        }
      }
      .layout__update-banner {
        position: fixed;
        right: 16px;
        bottom: 16px;
        z-index: 1100; /* por encima del dev badge */
        width: min(380px, calc(100vw - 32px));
        border-radius: var(--pvp-radius-md);
        background: var(--pvp-color-surface);
        box-shadow: var(--pvp-shadow-card);
      }
      .layout__dev-badge {
        position: fixed;
        right: 12px;
        bottom: 12px;
        z-index: 1000;
        padding: 3px 8px;
        border-radius: var(--pvp-radius-sm);
        border: 1px solid var(--pvp-color-warning);
        background: var(--pvp-color-warning-tint);
        color: var(--pvp-color-warning);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.08em;
        pointer-events: none;
      }
    `,
    ]
})
export class MainLayoutComponent {
  protected readonly auth = inject(AuthStore);
  protected readonly tenant = inject(TenantContextService);
  protected readonly realtime = inject(RealtimeConnectionService);
  protected readonly swUpdate = inject(SwUpdateService);
  protected readonly idle = inject(IdleTimeoutService);

  /** Colapso a rail (solo aplica en desktop). Persistido para sobrevivir full reloads. */
  protected readonly collapsed = signal(readStoredCollapsed());

  /** Drawer mobile (<lg) abierto sobre el contenido. Cerrado por defecto. */
  protected readonly mobileOpen = signal(false);

  private readonly desktopMq = window.matchMedia(DESKTOP_MQ);

  /** true en >=lg (sidenav estatico); false en mobile (drawer off-canvas). */
  protected readonly isDesktop = signal(this.desktopMq.matches);

  /** Nombre visible del usuario (ver resolveUserDisplayName). */
  protected readonly userDisplayName = computed(() =>
    resolveUserDisplayName(this.auth.user(), this.auth.isProfileLoading()),
  );

  /** Detalle para el tooltip del chip de usuario (email si hay; sino el id). */
  protected readonly userDetail = computed(() => {
    const user = this.auth.user();
    if (!user) {
      return '';
    }
    return user.email || user.id;
  });

  /**
   * Habilita el acceso a "Cambiar contrasena" en la topbar (LION-760). Solo en
   * modo local: los usuarios SSO cambian su contrasena en el IdP. `isLocalMode()`
   * lee el signal de config, asi que el computed reacciona si la config cambia.
   */
  protected readonly canChangePassword = computed(() => this.auth.isLocalMode());

  /** Nombre legible del tenant efectivo (fallback: UUID si no esta mapeado). */
  protected readonly tenantLabel = computed(() => {
    const id = this.tenant.tenantId();
    if (!id) {
      return '—';
    }
    return TENANT_LABELS[id] ?? id;
  });

  constructor() {
    // Un unico socket multiplexado para toda el area autenticada (idempotente).
    this.realtime.connect();

    const destroyRef = inject(DestroyRef);

    // Auto-logout por inactividad (LION-794). El shell autenticado es el unico
    // punto montado solo con sesion viva: arranca el monitoreo aca y lo corta al
    // destruirse (logout / salida del area). En dev-session NO se arranca:
    // logout() es no-op y el aviso quedaria sin efecto.
    if (!this.auth.isDevSession()) {
      this.idle.start();
      destroyRef.onDestroy(() => this.idle.stop());
    }

    // Cruce de breakpoint: al pasar a desktop el drawer mobile deja de existir.
    const onMqChange = (event: MediaQueryListEvent): void => {
      this.isDesktop.set(event.matches);
      if (event.matches) {
        this.mobileOpen.set(false);
      }
    };
    this.desktopMq.addEventListener('change', onMqChange);
    destroyRef.onDestroy(() => this.desktopMq.removeEventListener('change', onMqChange));

    // El drawer mobile se cierra al navegar (tap en un item del sidenav).
    inject(Router)
      .events.pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.mobileOpen.set(false));
  }

  /** ☰: en desktop alterna rail/expandido (persistido); en mobile abre/cierra el drawer. */
  protected toggleSidenav(): void {
    if (this.isDesktop()) {
      this.collapsed.update((c) => !c);
      writeStoredCollapsed(this.collapsed());
    } else {
      this.mobileOpen.update((open) => !open);
    }
  }

  protected closeMobileSidenav(): void {
    this.mobileOpen.set(false);
  }

  /** Activa la versión nueva de la PWA y recarga (fire-and-forget desde el template). */
  protected applyUpdate(): void {
    void this.swUpdate.applyUpdate();
  }
}
